import { useLiveCameraTrack } from "@common/hooks/useLiveCameraTrack"
import { setCameraStatus } from "@data/redux/actions/activity"
import {
    recordCameraSettings,
    resetCameraSettings,
    setLastPhoto,
    setPhotos,
} from "@data/redux/actions/camera"
import * as Device from "expo-device"
import { StatusBar } from "@common/systemBars/StatusBar"
import { LocalVideoTrack } from "livekit-client"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { Dimensions, ImageBackground, View } from "react-native"
import { IconButton } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"
import { connect, useDispatch } from "react-redux"
import Webcam from "react-webcam"
import { setRecentlyOpened } from "../../data/redux/actions/camera"
import TextM from "../components/text/TextM"
import { theme } from "../theme/theme"
import { DimensionsContext, IS_WEB, isWebMobile } from "../utils/mobileUtils"
import TouchableRippleM from "../components/button/TouchableRippleM"
import {
    isHandheld,
    listMediaDevices,
    preferredFacingMode,
} from "../../engine/shared/camera/cameraFacing"

export const CameraPreview = ({ photo, onConfirm, onRetry, imagePadding }) => {
    const dimensions = useContext(DimensionsContext)

    return (
        <View
            style={{
                backgroundColor: theme.colors.black,
                flex: 1,
                width: "100%",
            }}
        >
            <ImageBackground
                source={{ uri: photo?.uri ? photo?.uri : photo }}
                resizeMode={"contain"}
                imageStyle={{
                    marginTop: imagePadding / 2,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                }}
                style={{
                    height: photo?.height
                        ? (photo?.height * dimensions?.width) / photo?.width
                        : "100%",
                    backgroundColor: theme.colors.black,
                }}
            />
            <View
                style={{
                    flexDirection: "row",
                    position: "absolute",
                    bottom: 0,
                    alignSelf: "center",
                    justifyContent: "center",
                    width: "100%",
                }}
            >
                <TouchableRippleM
                    onPress={onRetry}
                    style={{
                        width: dimensions?.width / 2,
                        alignItems: "center",
                        paddingVertical: 25,
                        backgroundColor: theme.colors.blackTransparent,
                    }}
                >
                    <TextM
                        color={theme.colors.white}
                        fontWeight={"ExtraBold"}
                        fontSize={16}
                    >
                        Réessayer
                    </TextM>
                </TouchableRippleM>
                <TouchableRippleM
                    onPress={onConfirm}
                    style={{
                        width: dimensions?.width / 2,
                        alignItems: "center",
                        paddingVertical: 25,
                        backgroundColor: theme.colors.blackTransparent,
                    }}
                >
                    <TextM
                        color={theme.colors.white}
                        fontWeight={"ExtraBold"}
                        fontSize={16}
                    >
                        OK
                    </TextM>
                </TouchableRippleM>
            </View>
        </View>
    )
}

const CameraComponent = (props) => {
    const { navigation, route } = props
    const { formId = null, useLiveTrack = false } = route?.params || {}

    const cameraRef = useRef()
    const liveVideoElementRef = useRef(null)
    const dispatch = useDispatch()
    // The live track keeps the camera the call opened. Otherwise the rear
    // camera when the device has one, the front one if not (on a computer as
    // on a phone): read from the device list before the webcam opens, so it
    // opens once, on the right camera.
    const [facingMode, setFacingMode] = useState(useLiveTrack ? "user" : null)
    useEffect(() => {
        if (useLiveTrack) return
        let cancelled = false
        listMediaDevices().then((devices) => {
            if (!cancelled) {
                const handheld = isHandheld(
                    Device.deviceType,
                    Device.DeviceType.PHONE,
                    Device.DeviceType.TABLET
                )
                setFacingMode(
                    (mode) => mode ?? preferredFacingMode(devices, { handheld })
                )
            }
        })
        return () => {
            cancelled = true
        }
    }, [])
    const imagePadding = props.imagePaddingSaved
        ? parseInt(props.imagePaddingSaved)
        : 0

    const [previewVisible, setPreviewVisible] = useState(false)
    const [capturedImage, setCapturedImage] = useState(null)
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()

    // Only reuse the LiveKit video track when explicitly told to (camera was on).
    // When camera was off, useLiveTrack is false and we use the normal Webcam
    // flow — even if useLiveCameraTrack returns a stale track.
    const liveVideoTrack = useLiveCameraTrack()
    const isInLive = useLiveTrack && !!liveVideoTrack

    // Guard: if we navigated expecting a live track but it's gone
    // (e.g., room disconnection), close the camera instead of
    // falling through to the Webcam which wasn't initialised.
    useEffect(() => {
        if (useLiveTrack && !liveVideoTrack) {
            navigation.goBack()
        }
    }, [useLiveTrack, liveVideoTrack])

    // Single source of truth for clearing cameraOn: every way off this
    // screen — confirm, retry-then-back, the disconnect guard above, a
    // hardware back button, anything added later — unmounts this
    // component, so resetting here instead of at each call site can't be
    // missed by a future exit path.
    useEffect(() => {
        return () => dispatch(setCameraStatus(false))
    }, [])

    // Attach the LiveKit video track to the viewfinder each time it mounts: a
    // ref callback, not an effect, because the photo preview replaces the
    // viewfinder and "Réessayer" mounts a fresh, empty one while the track
    // stays the same. Detaching happens on the null call, not in a returned
    // cleanup: react-native-web's mergeRefs drops what a ref returns. Create +
    // mount the element BEFORE track.attach(el): play() on a still-detached
    // element is a documented pitfall on some browsers.
    const attachLiveVideo = useCallback(
        (container) => {
            if (!container) {
                const videoEl = liveVideoElementRef.current
                if (videoEl) {
                    liveVideoTrack.detach(videoEl)
                    videoEl.remove()
                    liveVideoElementRef.current = null
                }
                return
            }

            const videoEl = document.createElement("video")
            videoEl.style.width = "100%"
            videoEl.style.height = "100%"
            videoEl.style.objectFit = "cover"
            container.appendChild(videoEl)
            liveVideoTrack.attach(videoEl)
            liveVideoElementRef.current = videoEl
        },
        [liveVideoTrack]
    )

    const videoConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        // A preference, never a demand: a device without the camera asked for
        // opens the other one instead of a black screen.
        facingMode: { ideal: facingMode ?? "environment" },
        advanced: [
            { zoom: { ideal: 1.0 } },
            { brightness: { ideal: 100 } },
            { contrast: { ideal: 100 } },
            { whiteBalanceMode: { ideal: "auto" } },
            { exposureMode: { ideal: "auto" } },
            { focusMode: { ideal: "continuous" } },
            { frameRate: { ideal: 30 } },
        ],
    }

    const __takePicture = async () => {
        try {
            let imageSrc

            if (isInLive) {
                // Capture frame from the LiveKit video element via canvas
                const video = liveVideoElementRef.current
                // A viewfinder just remounted by "Réessayer" has no frame
                // yet: drawing it would preview an empty photo.
                if (!video?.videoWidth) return
                const canvas = document.createElement("canvas")
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                canvas.getContext("2d").drawImage(video, 0, 0)
                imageSrc = canvas.toDataURL("image/jpeg", 1.0)
            } else {
                if (!cameraRef?.current) return
                imageSrc = cameraRef.current.getScreenshot()
            }

            if (!imageSrc) {
                console.error("Failed to capture image")
                return
            }

            setCapturedImage(imageSrc)
            setPreviewVisible(true)
        } catch (error) {
            console.error("Error taking picture:", error)
        }
    }

    const onRetry = () => {
        setPreviewVisible(false)
        setCapturedImage(null)
    }

    const goBackFromCamera = () => {
        navigation.goBack()
        props.setRecentlyOpened(true)
    }

    const onConfirm = () => {
        props.setNewPhoto(formId, {
            uri: capturedImage,
            width: dimensions?.width,
            height: dimensions?.height,
        })
        goBackFromCamera()
    }

    const toggleCamera = async () => {
        const newMode = facingMode === "user" ? "environment" : "user"
        setFacingMode(newMode)

        // When in live mode, restart the LiveKit track with the new facing mode
        if (isInLive && liveVideoTrack instanceof LocalVideoTrack) {
            await liveVideoTrack.restartTrack({
                facingMode: newMode,
            })
        }
    }

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.black,
                paddingTop: insets.top,
                alignItems: "center",
                width: Dimensions.get("window").width,
            }}
        >
            {IS_WEB || !Device.isDevice ? (
                previewVisible && capturedImage ? (
                    <CameraPreview
                        photo={capturedImage}
                        imagePadding={imagePadding}
                        onConfirm={onConfirm}
                        onRetry={onRetry}
                    />
                ) : isInLive ? (
                    <View
                        ref={attachLiveVideo}
                        style={{
                            width: Dimensions.get("window").width,
                            height: "100%",
                        }}
                    />
                ) : useLiveTrack || facingMode == null ? // Live track expected but gone — render nothing while
                // the useEffect above navigates back. Or the device list is
                // still being read.
                null : (
                    <View
                        style={{
                            width: Dimensions.get("window").width,
                            height: "100%",
                        }}
                    >
                        <Webcam
                            height={dimensions?.height}
                            width={dimensions?.width}
                            ref={cameraRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={1.0}
                            forceScreenshotSourceSize={true}
                            videoConstraints={videoConstraints}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </View>
                )
            ) : null}

            {(!previewVisible || !capturedImage) && (
                <View
                    style={{
                        flexDirection: "row",
                        alignSelf: "center",
                        alignItems: "center",
                        alignContent: "center",
                        justifyContent: "center",
                        position: "absolute",
                        bottom: 0,
                        backgroundColor: theme.colors.blackTransparent,
                        height: imagePadding / 2 < 100 ? 100 : imagePadding / 2,
                        width: dimensions?.width,
                    }}
                >
                    <IconButton
                        icon="dot-circle"
                        iconColor={theme.colors.white}
                        containerColor={theme.colors.transparent}
                        size={60}
                        style={{
                            justifySelf: "center",
                            alignItems: "center",
                            alignSelf: "center",
                            position: "absolute",
                        }}
                        onPress={__takePicture}
                    />

                    {Device.isDevice && isWebMobile() && (
                        <TouchableRippleM
                            style={{
                                alignItems: "center",
                                position: "absolute",
                                right: 30,
                                backgroundColor: theme.colors.transparent,
                            }}
                            onPress={toggleCamera}
                        >
                            <FontAwesome6
                                name={"camera-rotate"}
                                size={40}
                                solid
                                color={theme.colors.white}
                            />
                        </TouchableRippleM>
                    )}
                </View>
            )}

            <View
                style={{
                    flexDirection: "row",
                    alignSelf: "center",
                    position: "absolute",
                    top: 0,
                    paddingTop: insets.top,
                    backgroundColor: theme.colors.blackTransparent,
                    height: imagePadding / 2 < 80 ? 80 : imagePadding / 2,
                    width: dimensions?.width,
                }}
            >
                <IconButton
                    icon="times"
                    iconColor={theme.colors.white}
                    containerColor={theme.colors.transparent}
                    size={32}
                    style={{ marginLeft: 10 }}
                    onPress={goBackFromCamera}
                />
            </View>
            <StatusBar
                style="light"
                backgroundColor={theme.colors.black}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        ratioSaved: state.camera?.ratio,
        imagePaddingSaved: state.camera?.imagePadding,
    }
}

const mapDispatchToProps = {
    setNewPhoto: setLastPhoto,
    recordCameraSettings,
    resetCameraSettings,
    setPhotos,
    setCameraStatus,
    setRecentlyOpened,
}

export default connect(mapStateToProps, mapDispatchToProps)(CameraComponent)
